import React, {useState} from 'react';
import {StyleSheet} from 'react-native';
import WebView from 'react-native-webview';
import {useFocusEffect} from '@react-navigation/native';
import {Dialog} from '@rneui/themed';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react';

import CatalogueAddToCartModal from '../../components/CatalogueAddToCartModal';
import ActivityIndicator from '../../components/ui-controls/ActivityIndicator';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {useStores} from '../../stores/use-stores';
import rewardsTheme from '../../theme/rewardsTheme';
import apiService from '../../transport/apiService';
import config from '../../config';
// import WebFooter from '../../components/WebFooter';

type CatalogueEventData = {
  action?: string;
  productSKU?: string;
};

const isCatalogueEventData = (value: unknown): value is CatalogueEventData =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  'action' in value;

// In Web with WebView, you may see errors like this in the browser console:
//
// loader.min.js?version=x:1 Uncaught DOMException: Failed to execute 'pushState' on 'History':
// A history state object with URL 'http://localhost:8080/CATALOGUES/CATALOGUES_T/srcdoc?c=spring-the-season-of-wins-nsw-z1&page=3'
// cannot be created in a document with origin 'http://localhost:8080' and URL 'about:srcdoc'.
// at updateURL (https://nexuspublications.com.au/a10/publications/loader.min.js?version=x:1:15651)
// at https://nexuspublications.com.au/a10/publications/loader.min.js?version=x:1:3958
//
// This is because the Nexus script try to do `history.pushState` to update the URL with something
// like `?c=spring-the-season-of-wins-nsw-z3&page=1`. I don't think that URL update is needed here.

const CatalogueScreen = observer(() => {
  const [catalogueHtml, setCatalogueHtml] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>();
  const [isDialogVisible, setIsDialogVisible] = useState(false); // State to control the dialog visibility
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const {
    shopSelectionStore: {selectedStore, shopSalesforceId},
  } = useStores();

  const storeId = shopSalesforceId;
  const storeState = selectedStore?.address?.administrative_area?.toUpperCase();

  useFocusEffect(
    React.useCallback(() => {
      async function fetchData() {
        const html = await apiService.getCatalogueHtml(storeId, storeState);
        setCatalogueHtml(html);
      }
      if (storeId && storeState) {
        fetchData();
      }
    }, [storeId, storeState]),
  );

  const platformStyles = StyleSheet.create({
    main: {
      ...(isWebDesktopScreen ? {} : {paddingHorizontal: 0}),
    },
  });

  const handleDialogClose = () => setIsDialogVisible(false);
  const handleWebViewMessage = (event: any) => {
    console.debug('👾', 'handleWebViewMessage', 'event', event);
    const eventData = getWebViewEventData(event);
    if (
      isCatalogueEventData(eventData) &&
      eventData?.action === 'openProductView'
    ) {
      handleCatalogueMessage(eventData);
    }
  };

  const handleCatalogueMessage = (event: CatalogueEventData) => {
    console.log('👾', 'handleCatalogueMessage', 'event', event);
    if (event.productSKU) {
      const productSKUs = event.productSKU.split(',');
      setSelectedProductIds(productSKUs);
      setIsDialogVisible(true);
    } else console.error('No productSKU in the catalogue event data:', event);
  };

  return (
    <ThemeProvider theme={rewardsTheme}>
      <ContentView testID="catalogue-screen" style={platformStyles.main}>
        {catalogueHtml ? (
          <WebView
            cacheEnabled={false}
            scrollEnabled
            startInLoadingState
            source={{
              html: catalogueHtml,
            }}
            style={styles.webview}
            onMessage={handleWebViewMessage}
            /** For remote debugging {@link https://developer.chrome.com/docs/devtools/remote-debugging} */
            webviewDebuggingEnabled={config.ENV === 'DEV' || config.ENV === 'BETA'}
          />
        ) : (
          <ActivityIndicator />
        )}
        {/* TODO: The WebFooter should be in a global/common place */}
        {/* {!!isWebDesktopScreen && <WebFooter />} */}

        <Dialog
          isVisible={isDialogVisible}
          onBackdropPress={handleDialogClose}
          overlayStyle={styles.dialogOverlay}>
          <Dialog.Title title={'ADD TO CART'} titleStyle={styles.dialogTitle} />
          <CatalogueAddToCartModal
            onClose={handleDialogClose}
            productIds={selectedProductIds || []}
          />
        </Dialog>
      </ContentView>
    </ThemeProvider>
  );
});

export default CatalogueScreen;

const styles = StyleSheet.create({
  webview: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  dialogOverlay: {
    width: '90%',
    maxWidth: 600,
    maxHeight: '80%',
    padding: 20,
    position: 'relative',
  },
  dialogTitle: {
    textAlign: 'center',
  },
});

const getWebViewEventData = (event: any) => {
  let eventData;
  /**
   * Sometimes the WebView event data is a {@type string}
   * 
   * This is likely because the react-native-webview for 
   * IOS/Android can only send strings
   * {@link https://github.com/react-native-webview/react-native-webview/blob/master/docs/Reference.md#postmessagestr}
   */
  if (typeof event.nativeEvent.data === 'string') {
    try {
      eventData = JSON.parse(event.nativeEvent.data);
    } catch (error) {
      console.warn('Failed to parse WebView message data:', error);
      eventData = event.nativeEvent.data;
    }
  } else {
    eventData = event.nativeEvent.data;
  }
  return eventData;
};
